import { useMutation } from '@tanstack/react-query'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { formToObject } from 'lib/forms/formToObject'
import { rem } from 'polished'
import type { FormEventHandler } from 'react'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import requestPostVerifyAgentHubSocieteIdentity from 'agentHub/api/requestPostVerifySocieteIdentity'
import requestPostLinkSociete from 'agentHub/api/requestPostLinkSociete'
import type { LinkSocieteParams } from 'agentHub/api/requestPostLinkSociete'
import { SET_SOCIETE_REQUESTS_INTERACTIONS } from 'actions/actionConstants'
import { useAppDispatch } from 'hooks/reduxHooks'

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "firstName lastName"
                         "identifier identifier";
    .grid-firstName {
      grid-area: firstName;
    }
    .grid-lastName {
      grid-area: lastName;
    }
    .grid-identifier {
      grid-area: identifier;
    }
  }
`

interface IdentifierFormData {
  firstName: string
  lastName: string
  identifier: string
}

interface Props {
  onSuccess: () => void
  onEmailSent: () => void
  onCreateNew: (prefillEmail?: string) => void
}

type InputMode = 'societeNumber' | 'email'

type VerifyOutcome =
  | { kind: 'notFound'; identifier: string; inputMode: InputMode }
  | { kind: 'existsNoLoyalty'; identifier: string; inputMode: InputMode }

function buildLinkParams(identifier: string, mode: InputMode): LinkSocieteParams {
  return mode === 'societeNumber'
    ? { societeNumber: identifier }
    : { email: identifier }
}

function SocieteLinkExistingForm({ onSuccess, onEmailSent, onCreateNew }: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const [isError, setIsError] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [inputMode, setInputMode] = useState<InputMode>('societeNumber')
  const [verifyOutcome, setVerifyOutcome] = useState<VerifyOutcome | undefined>()

  const dispatchEngaged = useCallback(() => {
    dispatch({
      type: SET_SOCIETE_REQUESTS_INTERACTIONS,
      interactions: { engaged: { engagedAt: new Date().toISOString() } },
    })
  }, [dispatch])

  const linkMutation = useMutation({
    mutationFn: async(params: LinkSocieteParams) => {
      return requestPostLinkSociete(params)
    },
    onSuccess: (result) => {
      setIsError(false)
      dispatchEngaged()
      if (result.confirmationRequired) {
        onEmailSent()
      } else {
        onSuccess()
      }
    },
    onError: (error: App.ApiCallFailure | Error) => {
      setErrorMsg(error.message)
      setIsError(true)
    },
  })

  const verifyMutation = useMutation({
    mutationFn: async(data: { identifier: string; firstName: string; lastName: string }) => {
      const identifierParams = buildLinkParams(data.identifier, inputMode)
      const result = await requestPostVerifyAgentHubSocieteIdentity({
        ...identifierParams,
        firstName: data.firstName,
        lastName: data.lastName,
      })
      return { ...result, identifier: data.identifier, inputMode }
    },
    onSuccess: (result) => {
      setIsError(false)

      if (!result.exists) {
        setVerifyOutcome({ kind: 'notFound', identifier: result.identifier, inputMode: result.inputMode })
        return
      }

      if (!result.loyaltyAccountExists) {
        setVerifyOutcome({ kind: 'existsNoLoyalty', identifier: result.identifier, inputMode: result.inputMode })
        return
      }

      linkMutation.mutate(buildLinkParams(result.identifier, result.inputMode))
    },
    onError: (error: App.ApiCallFailure | Error) => {
      setErrorMsg(error.message)
      setIsError(true)
    },
  })

  const handleVerifySubmit = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    setIsError(false)
    setVerifyOutcome(undefined)
    const formData = formToObject<IdentifierFormData>(e.currentTarget)
    verifyMutation.mutate({
      identifier: formData.identifier.trim(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
    })
  }, [verifyMutation])

  const handleLinkFromOutcome = useCallback(() => {
    if (verifyOutcome?.kind !== 'existsNoLoyalty') return
    linkMutation.mutate(buildLinkParams(verifyOutcome.identifier, verifyOutcome.inputMode))
  }, [linkMutation, verifyOutcome])

  const handleCreateNew = useCallback(() => {
    if (verifyOutcome?.kind !== 'notFound') return
    const prefillEmail = verifyOutcome.inputMode === 'email' ? verifyOutcome.identifier : undefined
    onCreateNew(prefillEmail)
  }, [verifyOutcome, onCreateNew])

  const handleReset = useCallback(() => {
    setVerifyOutcome(undefined)
    setIsError(false)
    setErrorMsg('')
  }, [])

  const toggleInputMode = useCallback(() => {
    setInputMode(prev => prev === 'societeNumber' ? 'email' : 'societeNumber')
    setIsError(false)
    setErrorMsg('')
    setVerifyOutcome(undefined)
  }, [])

  const toggleLabel = inputMode === 'societeNumber'
    ? intl.formatMessage({
        id: '9BnEjCbakwuOlAZDV-mR',
        defaultMessage: 'Use email address instead',
        description: 'Link to switch to email input mode for Société account linking',
      })
    : intl.formatMessage({
        id: '22ilkRESLMTXzF5Gqfta',
        defaultMessage: 'Use member number instead',
        description: 'Link to switch to member number input mode for Société account linking',
      })

  const isBusy = verifyMutation.isLoading || linkMutation.isLoading
  const notFoundMessage = verifyOutcome?.inputMode === 'societeNumber'
    ? intl.formatMessage({
        id: '9CUmxrIDAgU-C7HhBAIJ',
        defaultMessage: 'No account found with the provided member number. A new account will need to be created.',
        description: 'Message shown when Société verification cannot find an existing user by member number',
      })
    : intl.formatMessage({
        id: 'VWR_ospqbMFkj7DWMXAS',
        defaultMessage: 'No account found with the provided email address. A new account will need to be created.',
        description: 'Message shown when Société verification cannot find an existing user by email address',
      })
  const linkExistingDescription = inputMode === 'societeNumber'
    ? intl.formatMessage({
        id: 'ey3XNuYKJqkelXaLYyoK',
        defaultMessage: 'Enter your name and the Société member number to link.',
        description: 'Description shown on the Société link-existing-account form when member number input mode is active',
      })
    : intl.formatMessage({
        id: 'pj7w65YcakSg2heQ-2il',
        defaultMessage: 'Enter your name and the Société email address to link.',
        description: 'Description shown on the Société link-existing-account form when email input mode is active',
      })

  if (verifyOutcome?.kind === 'notFound') {
    return (
      <Group direction="vertical" gap={20}>
        <MessageBanner kind="warning">
          {notFoundMessage}
        </MessageBanner>
        <Group direction="vertical" gap={12}>
          <TextButton fit="mobile-full-width" kind="primary" size="large" onClick={handleCreateNew}>
            {intl.formatMessage({
              id: 'Hs6vkylI5pttFCeFiCqO',
              defaultMessage: 'Create a new account',
              description: 'Button to navigate to the create new Société account form when no existing user is found',
            })}
          </TextButton>
          <TextButton fit="mobile-full-width" kind="tertiary" size="medium" onClick={handleReset}>
            {intl.formatMessage({
              id: 'B2Bx81Hr3J9qzlQpBUBQ',
              defaultMessage: 'Try a different identifier',
              description: 'Button to go back and try a different identifier for Société account linking',
            })}
          </TextButton>
        </Group>
      </Group>
    )
  }

  if (verifyOutcome?.kind === 'existsNoLoyalty') {
    return (
      <Group direction="vertical" gap={20}>
        {isError && (
          <MessageBanner kind="critical">
            {errorMsg}
          </MessageBanner>
        )}
        <MessageBanner kind="informational-primary">
          {intl.formatMessage({
            id: 'hgtKGDOxvkpqXyAhy1wN',
            defaultMessage: 'An account was found, but it does not have a Société loyalty membership yet. A loyalty account will be created when the link is confirmed.',
            description: 'Info message when the user exists but has no loyalty account during Société linking',
          })}
        </MessageBanner>
        <Group direction="vertical" gap={12}>
          <TextButton fit="mobile-full-width" kind="primary" size="large" onClick={handleLinkFromOutcome} disabled={isBusy}>
            {intl.formatMessage({
              id: 'kE1g4i-K5Obo-NmNcO-3',
              defaultMessage: 'Continue linking',
              description: 'Button to proceed with linking when user exists but has no loyalty account',
            })}
          </TextButton>
          <TextButton fit="mobile-full-width" kind="tertiary" size="medium" onClick={handleReset}>
            {intl.formatMessage({
              id: 'B2Bx81Hr3J9qzlQpBUBQ',
              defaultMessage: 'Try a different identifier',
              description: 'Button to go back and try a different identifier for Société account linking',
            })}
          </TextButton>
        </Group>
      </Group>
    )
  }

  return (
    <Group direction="vertical" gap={20}>
      {isError && (
        <MessageBanner kind="critical">
          {errorMsg}
        </MessageBanner>
      )}
      <BodyText variant="medium">
        {linkExistingDescription}
      </BodyText>

      <form onSubmit={handleVerifySubmit} name="societeLinkExistingForm">
        <Group direction="vertical" gap={16}>
          <InputGrid>
            <TextInput
              className="grid-firstName"
              label={intl.formatMessage({
                id: 'a6N52vR0bv1c8qSD0F6Q',
                defaultMessage: 'First name',
                description: 'Label for first name input in Société account creation',
              })}
              name="firstName"
              pattern="^[A-Za-z ]+$"
              invalidErrorMessage={intl.formatMessage({
                id: '_c09YlOlkCh2PAtzyo5m',
                defaultMessage: 'Invalid characters. Please use only letters and spaces',
                description: 'Validation error for name fields with invalid characters in Société account creation',
              })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: '283tPBm2q3imViCI54dl',
                defaultMessage: 'First name is required',
                description: 'Required validation error for first name in Société account creation',
              })}
            />
            <TextInput
              className="grid-lastName"
              label={intl.formatMessage({
                id: '9EGkVf1Bm6XlFq7T9FCb',
                defaultMessage: 'Last name',
                description: 'Label for last name input in Société account creation',
              })}
              name="lastName"
              pattern="^[A-Za-z ]+$"
              invalidErrorMessage={intl.formatMessage({
                id: '_c09YlOlkCh2PAtzyo5m',
                defaultMessage: 'Invalid characters. Please use only letters and spaces',
                description: 'Validation error for name fields with invalid characters in Société account creation',
              })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'KA_JX5eIFWmKYGrrIRaw',
                defaultMessage: 'Last name is required',
                description: 'Required validation error for last name in Société account creation',
              })}
            />
            {inputMode === 'societeNumber' && (
              <TextInput
                className="grid-identifier"
                label={intl.formatMessage({
                  id: 'YAkTs1PMi-3_VGS8faEP',
                  defaultMessage: 'Société member number',
                  description: 'Label for the Société member number input field when linking Société account',
                })}
                name="identifier"
                pattern="^[0-9]+$"
                invalidErrorMessage={intl.formatMessage({
                  id: '-qD6fbABtmF3sygMUTx3',
                  defaultMessage: 'Please enter a valid member number (numbers only)',
                  description: 'Validation error for invalid Société member number format',
                })}
                placeholder={intl.formatMessage({
                  id: 'y1Jr9679LHuzbPNwGELL',
                  defaultMessage: 'e.g. 123456',
                  description: 'Placeholder for the Société member number input field',
                })}
                required
                requiredErrorMessage={intl.formatMessage({
                  id: '0nxR5MPbdseBCj-lVc3N',
                  defaultMessage: 'Member number is required',
                  description: 'Required validation error for Société member number input',
                })}
              />
            )}
            {inputMode === 'email' && (
              <TextInput
                className="grid-identifier"
                label={intl.formatMessage({
                  id: 'XoQ2c-0yCfM7D2ENMjkg',
                  defaultMessage: 'Email address',
                  description: 'Label for the email input field when linking Société account',
                })}
                name="identifier"
                type="email"
                invalidErrorMessage={intl.formatMessage({
                  id: 'ZRuTVe1qXRtk2BEhtXqC',
                  defaultMessage: 'Please enter a valid email address',
                  description: 'Validation error for invalid email format in Société linking',
                })}
                placeholder={intl.formatMessage({
                  id: '8re7-gu6Ohw6KnxHHLBW',
                  defaultMessage: 'Enter your email address',
                  description: 'Placeholder for the email input field when linking Société account',
                })}
                required
                requiredErrorMessage={intl.formatMessage({
                  id: '_94ICA5b41-11XMyFMuV',
                  defaultMessage: 'Email address is required',
                  description: 'Required validation error for email input in Société linking',
                })}
              />
            )}
          </InputGrid>
          <TextButton kind="tertiary" size="small" onClick={toggleInputMode} type="button">
            {toggleLabel}
          </TextButton>
          <TextButton disabled={isBusy} fit="mobile-full-width" kind="primary" size="large" type="submit">
            {intl.formatMessage({
              id: 'BtUI9nrJ3Lbch9iTwje2',
              defaultMessage: 'Link account',
              description: 'Submit button for linking an existing Société account',
            })}
          </TextButton>
          <TextButton fit="mobile-full-width" kind="tertiary" size="medium" onClick={() => onCreateNew()} type="button">
            {intl.formatMessage({
              id: '4ZwRwD9pzt6K-tyLOF9A',
              defaultMessage: "Don't have an account? Create one",
              description: 'Button to navigate to create a new Société account from the link form',
            })}
          </TextButton>
        </Group>
      </form>
    </Group>
  )
}

export default SocieteLinkExistingForm
